import { useMemo } from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    CartesianGrid,
    Scatter,
    Bar,
    Label,
} from "recharts";
import { getDateAggregationKey } from "../../../utils/getDateAggregationKey";
import { downsample } from "../../../utils/performance";

interface Props {
    mergedData: any[];
    relevant: any[];
    alignMode: "none" | "left" | "right";
    connectAcrossNulls: boolean;
    hoveredKey: string | null;
    setHoveredKey: (key: string | null) => void;
    seriesConfigs: Record<string, string>;
    getColor: (i: number) => string;
}

export function MultiLineChart({
    mergedData,
    relevant,
    alignMode,
    connectAcrossNulls,
    hoveredKey,
    setHoveredKey,
    seriesConfigs,
    getColor
}: Props) {
    const chartData = useMemo(
        () => (mergedData.length > 1500 ? downsample(mergedData, 1000) : mergedData),
        [mergedData]
    );

    return (
        <div style={{ width: "100%", height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                    <XAxis
                        dataKey={alignMode === "none" ? "time" : "index"}
                        tickFormatter={(v) =>
                            alignMode === "none" ? getDateAggregationKey(v, "datetime") : `${v}`
                        }
                        stroke="var(--text-muted)"
                        fontSize={12}
                    />

                    {/* Dynamic Y-Axes */}
                    <YAxis yAxisId="left" orientation="left" domain={["auto", "auto"]} stroke="var(--text-muted)" fontSize={12} width={60}>
                        <Label
                            value="Primary (L)"
                            angle={-90}
                            position="insideLeft"
                            style={{ textAnchor: 'middle', fill: 'var(--text-muted)', fontSize: 10, fontWeight: 500 }}
                        />
                    </YAxis>
                    <YAxis yAxisId="right" orientation="right" domain={["auto", "auto"]} stroke="var(--text-muted)" fontSize={12} width={60}>
                        <Label
                            value="Secondary (R)"
                            angle={90}
                            position="insideRight"
                            style={{ textAnchor: 'middle', fill: 'var(--text-muted)', fontSize: 10, fontWeight: 500 }}
                        />
                    </YAxis>

                    <Tooltip
                        contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--surface-glass-border)', borderRadius: 'var(--radius-sm)' }}
                        labelStyle={{ color: 'var(--text-primary)', fontWeight: 'bold' }}
                        labelFormatter={(label) =>
                            alignMode === "none"
                                ? getDateAggregationKey(label, "datetime")
                                : `Index ${label}`
                        }
                        animationDuration={0}
                    />

                    <Legend
                        content={(props: any) => {
                            const { payload } = props;
                            return (
                                <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", justifyContent: "center", marginTop: '1rem' }}>
                                    {payload.map((entry: any, index: number) => {
                                        const s = relevant.find(rel => rel.id === entry.value || rel.name === entry.value);
                                        const axis = s ? seriesConfigs[s.id] : null;
                                        const axisLabel = axis === "left" ? "(L)" : axis === "right" ? "(R)" : "";

                                        return (
                                            <span
                                                key={`legend-item-${index}`}
                                                onMouseEnter={() => setHoveredKey(s?.id || entry.value)}
                                                onMouseLeave={() => setHoveredKey(null)}
                                                style={{
                                                    cursor: "pointer",
                                                    fontSize: '0.85rem',
                                                    fontWeight: hoveredKey === (s?.id || entry.value) ? "bold" : "normal",
                                                    opacity: hoveredKey && hoveredKey !== (s?.id || entry.value) ? 0.5 : 1,
                                                    color: entry.color,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.color }} />
                                                {entry.value} <span style={{ opacity: 0.6, fontSize: '0.7rem' }}>{axisLabel}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            );
                        }}
                    />
                    {relevant.map((s, idx) => {
                        const color = getColor(idx);
                        const isActive = hoveredKey === null || hoveredKey === s.id;
                        const nature = s.nature || "generic";

                        // 1. Determine Display Type (Auto-Select)
                        let displayType = s.displayType;
                        if (!displayType) {
                            if (nature === "volume") displayType = "bar";
                            else if (nature === "signal") displayType = "arrow"; // mapping 'signal' to 'star' below
                            else displayType = "line";
                        }

                        // 2. Determine Y-Axis (Auto-Scaling)
                        const yAxisId = seriesConfigs[s.id] || "left";

                        if (displayType === "bar") {
                            return (
                                <Bar
                                    key={s.id}
                                    yAxisId={yAxisId}
                                    dataKey={s.id}
                                    fill={color}
                                    name={s.name}
                                    opacity={isActive ? 1 : 0.3}
                                    onMouseEnter={() => setHoveredKey(s.id)}
                                    onMouseLeave={() => setHoveredKey(null)}
                                    isAnimationActive={false}
                                />
                            );
                        }

                        if (displayType === "arrow" || (nature === "signal" && !s.displayType)) {
                            return (
                                <Scatter
                                    key={s.id}
                                    yAxisId={yAxisId}
                                    dataKey={s.id}
                                    fill={color}
                                    name={s.name}
                                    shape="star"
                                    opacity={isActive ? 1 : 0.3}
                                    onMouseEnter={() => setHoveredKey(s.id)}
                                    onMouseLeave={() => setHoveredKey(null)}
                                    isAnimationActive={false}
                                />
                            );
                        }

                        return (
                            <Line
                                key={s.id}
                                yAxisId={yAxisId}
                                type={chartData.length > 800 ? "linear" : "monotone"}
                                dataKey={s.id}
                                stroke={color}
                                dot={false}
                                connectNulls={connectAcrossNulls}
                                name={s.name}
                                opacity={isActive ? 1 : 0.3}
                                strokeWidth={isActive ? 2 : 1}
                                onMouseEnter={() => setHoveredKey(s.id)}
                                onMouseLeave={() => setHoveredKey(null)}
                                isAnimationActive={false}
                            />
                        );
                    })}

                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

